import React, { useEffect, useState, useRef } from 'react';
import { Citation } from '../../types/citation';

interface CitationTooltipProps {
  citation: Citation | null;
  position?: { x: number; y: number };
}

const CitationTooltip: React.FC<CitationTooltipProps> = ({ citation, position }) => {
  const [isOpen, setIsOpen] = useState(false);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isPointerInsideRef = useRef(false);

  // Clear all timers helper
  const clearTimers = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  // Main effect: handle citation changes
  useEffect(() => {
    clearTimers();

    if (citation) {
      // Show with delay (hover intent pattern)
      showTimeoutRef.current = setTimeout(() => {
        setIsOpen(true);
        showTimeoutRef.current = null;
      }, 300); // 300ms delay feels natural
    } else {
      // Hide with shorter delay
      hideTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
        hideTimeoutRef.current = null;
      }, 150);
    }

    return clearTimers;
  }, [citation]);

  // Scroll handler: immediately hide on any scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => {
      clearTimers();
      setIsOpen(false);
    };

    // Use capture phase to catch all scroll events including in scroll containers
    window.addEventListener('scroll', handleScroll, true);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // Pointer event handlers for tooltip itself (to keep it open when hovering over it)
  const handleTooltipPointerEnter = () => {
    isPointerInsideRef.current = true;
    clearTimers();
  };

  const handleTooltipPointerLeave = () => {
    isPointerInsideRef.current = false;
    if (!citation) {
      hideTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
        hideTimeoutRef.current = null;
      }, 150);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return clearTimers;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return clearTimers;
  }, []);

  if (!citation || !isOpen) {
    return null;
  }

  // Collect all unique primary sources from sourceInfo
  const allPrimarySources = new Set<string>();
  if (citation.sourceInfo) {
    citation.sourceInfo.forEach(source => {
      if (source.primarySources && Array.isArray(source.primarySources)) {
        source.primarySources.forEach(ps => allPrimarySources.add(ps));
      }
    });
  }

  return (
    <div
      ref={tooltipRef}
      className="fixed z-50 max-w-md p-4 text-sm bg-white dark:bg-dark-primary border border-neutral-dark dark:border-dark-border rounded-lg shadow-lg"
      style={{
        left: position?.x || 0,
        top: (position?.y || 0) - 10,
        transform: 'translateY(-100%)',
        pointerEvents: 'auto', // Allow interaction with tooltip
      }}
      onPointerEnter={handleTooltipPointerEnter}
      onPointerLeave={handleTooltipPointerLeave}
    >
      <div className="font-medium text-text-primary dark:text-dark-textPrimary mb-3">
        {citation.title ? citation.title : `Citation: ${citation.id}`}
      </div>
      
      {/* List of Sources */}
      {allPrimarySources.size > 0 ? (
        <div className="mb-3">
          <div className="text-xs font-medium text-text-secondary dark:text-dark-textSecondary mb-2 uppercase tracking-wide">
            List of Sources:
          </div>
          <ul className="space-y-1">
            {Array.from(allPrimarySources).map((source, index) => (
              <li key={index} className="flex items-start">
                <span className="text-text-tertiary dark:text-dark-textTertiary mr-2">•</span>
                <span className="text-text-secondary dark:text-dark-textSecondary text-xs">
                  {source}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-text-tertiary dark:text-dark-textTertiary text-xs mb-3">
          No source information available
        </div>
      )}
      
      <div className="text-xs text-text-tertiary dark:text-dark-textTertiary">
        Click for full details
      </div>
    </div>
  );
};

export default CitationTooltip;
